// src/api/adduser.js
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { notifySuccess, notifyError } from "../../utils/ToastifyProvider";
import { useNavigate } from "react-router";
import { useLoader } from "../../utils/LoaderContext";

// const BASE_URL = "https://tcs.teamexbiz.in/api/api";
const BASE_URL = process.env.REACT_APP_API_BASE_URL;
//  const BASE_URL = "http://localhost:4000/api";

const createUser = async (data) => {
  const token = localStorage.getItem("token");
  const payload = {
    username: data.username,
    password: data.password,
    email: data.email,
    role: "employee",
    firstName: data.firstName,
    lastName: data.lastName,
    position: data.position,
    department: data.department,
    mobile: data.mobile,
    gender: data.gender,
  };

  const response = await axios.post(`${BASE_URL}/admin/create-user`, payload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

export const useCreateUser = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      setLoading(true); // ✅ Start
      try {
        return await createUser(data);
      } finally {
        setLoading(false); // ✅ End
      }
    },
    onSuccess: (data) => {
      setLoading(true);
      // console.log("User created successfully:", data);
      notifySuccess("User created successfully");
      setLoading(false);
      navigate("/employee");
      queryClient.invalidateQueries(["users"]);
    },
    onError: (error) => {
      // console.error("Error creating user:", error);
      notifyError(error?.response?.data?.message || "Failed to create user");
    },
  });
};

export const getAllUsers = async (search = "") => {
  const token = localStorage.getItem("token");
  const response = await axios.get(`${BASE_URL}/admin/all-users`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      search, // dynamically pass search param
    },
  });

  return response.data; // plain array
};

export const useGetAllUsers = (search = "") => {
  return useQuery({
    queryKey: ["users", search],
    queryFn: () => getAllUsers(search), // ✅ wrap in a function
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true,
    onError: (error) => {
      // console.error("Error fetching users:", error);
      notifyError("Failed to fetch users");
    },
  });
};

const changePassword = async ({ oldPassword, newPassword }) => {
  const token = localStorage.getItem("token");

  const response = await axios.patch(
    `${BASE_URL}/admin/change-password`,
    {
      oldPassword,
      newPassword,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: changePassword,
    onSuccess: (data) => {
      notifySuccess(data?.message || "Password changed successfully!");
    },
    onError: (error) => {
      notifyError(error.response?.data?.message || "Failed to change password");
    },
  });
};

export const getProfile = async () => {
  const token = localStorage.getItem("token");

  const response = await axios.get(`${BASE_URL}/auth/profile`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};

// ✅ React Query Hook for Profile
export const useGetProfile = () => {
  return useQuery({
    queryKey: ["profile"],
    queryFn: getProfile,
    staleTime: 1000 * 60 * 5, // cache for 5 mins
    onError: (error) => {
      notifyError(error.response?.data?.message || "Failed to fetch profile");
    },
  });
};

const updateUser = async ({ userId, data }) => {
  const token = localStorage.getItem("token");

  const payload = {
    firstName: data.firstName,
    lastName: data.lastName,
    position: data.position,
    department: data.department,
    username: data.username,
    email: data.email,
    mobile: data.mobile,
    gender: data.gender,
    role: data.role,
  };

  const response = await axios.put(
    `${BASE_URL}/admin/edit-user/${userId}`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response.data;
};

export const useUpdateUser = () => {
  const { setLoading } = useLoader();
  const navigate = useNavigate();
  const queryClient = useQueryClient(); // ✅ Add this if using React Query

  return useMutation({
    mutationFn: async (params) => {
      setLoading(true);
      try {
        return await updateUser(params);
      } finally {
        setLoading(false);
      }
    },
    onSuccess: () => {
      notifySuccess("User updated successfully");
      
      // ✅ OPTION 1: Invalidate queries instead of hard reload (RECOMMENDED)
      queryClient.invalidateQueries(['users']); // or whatever your query key is
      navigate("/employee");
      
      // ✅ OPTION 2: If you must use reload, remove it from here
      // window.location.reload(); // ⚠️ This causes a hard reload and loses React state
    },
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to update user");
    },
  });
};

const deleteUser = async (userId) => {
  const token = localStorage.getItem("token");
  const res = await axios.delete(`${BASE_URL}/auth/delete-user/${userId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data;
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { setLoading } = useLoader();

  return useMutation({
    mutationFn: async (userId) => {
      setLoading(true); // ✅ Start
      try {
        return await deleteUser(userId);
      } finally {
        setLoading(false); // ✅ End
      }
    },
    onSuccess: () => {
      notifySuccess("User deleted successfully");
      queryClient.refetchQueries(["users"]);
    },
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to delete user");
    },
  });
};

export const useUploadProfilePic = () => {
  const token = localStorage.getItem("token");
  return useMutation({
    mutationFn: async ({ id, file }) => {
      const formData = new FormData();
      formData.append("profilePic", file);

      const response = await axios.post(
        `${BASE_URL}/admin/upload-profilepic/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      notifySuccess("Profile picture updated successfully!");
    },
    onError: (error) => {
      notifyError(
        error?.response?.data?.message || "Failed to update profile picture"
      );
    },
  });
};

export const useDeleteProfilePic = () => {
  const token = localStorage.getItem("token");

  return useMutation({
    mutationFn: async ({ id }) => {
      const res = await axios.delete(`${BASE_URL}/admin/reprofilepic/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res.data;
    },
    onSuccess: (data) => {
      notifySuccess(data?.message || "Profile picture removed successfully");
    },
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to remove profile picture");
    },
  });
};


export const useAddTask = () => {
  const queryClient = useQueryClient();
  const token = localStorage.getItem("token");

  return useMutation({
    mutationFn: async ({ userId, title, description, date }) => {
      const res = await axios.post(
        `${BASE_URL}/task/add-task`,
        { userId, title, description, date },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["tasks"]);
      queryClient.invalidateQueries(["userTasks"]);
      queryClient.invalidateQueries(["dateTasks"]);
      notifySuccess("Task added successfully");
    },
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to add task");
    },
  });
};

// Get All Tasks with Pagination
export const useGetAllTasks = ({ page = 1, limit = 10, month, year }) => {
  const token = localStorage.getItem("token");

  return useQuery({
    queryKey: ["tasks", page, limit, month, year],
    queryFn: async () => {
      const res = await axios.get(`${BASE_URL}/task/get-tasks`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit, month, year }, // ✅ send pagination + filters
      });
      return res.data;
    },
    keepPreviousData: true, // ✅ helps smooth pagination
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to fetch tasks");
    },
  });
};

// Get Tasks by User with Pagination
export const useGetTasksByUser = ({ userId, page = 1, limit , month, year, enabled = true }) => {
  const token = localStorage.getItem("token");

  return useQuery({
    queryKey: ["userTasks", userId, page, limit, month, year],
    queryFn: async () => {
      const params = { page, limit };
      
      // Add month and year if provided
      if (month && year) {
        params.month = month;
        params.year = year;
      }

      const res = await axios.get(`${BASE_URL}/task/user-task/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return res.data;
    },
    enabled: !!userId && enabled,
    keepPreviousData: true,
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to fetch user tasks");
    },
  });
};

// Get Tasks by Date with Pagination
export const useGetTasksByDate = ({ date, page = 1, limit = 10 }) => {
  const token = localStorage.getItem("token");

  return useQuery({
    queryKey: ["dateTasks", date, page, limit],
    queryFn: async () => {
      const res = await axios.get(`${BASE_URL}/task/datewise-task/${date}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit },
      });
      return res.data;
    },
    enabled: !!date,
    keepPreviousData: true,
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to fetch tasks by date");
    },
  });
};

// Get Tasks by Department with Pagination
export const useGetTasksByDepartment = ({ department, page = 1, limit = 10 }) => {
  const token = localStorage.getItem("token");

  return useQuery({
    queryKey: ["departmentTasks", department, page, limit],
    queryFn: async () => {
      const res = await axios.get(`${BASE_URL}/task/department-task/${department}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit },
      });
      return res.data;
    },
    enabled: !!department,
    keepPreviousData: true,
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to fetch department tasks");
    },
  });
};

// Edit Task
export const useEditTask = () => {
  const queryClient = useQueryClient();
  const token = localStorage.getItem("token");

  return useMutation({
    mutationFn: async ({ taskId, ...updateData }) => {
      const res = await axios.put(
        `${BASE_URL}/task/edit-task/${taskId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["tasks"]);
      queryClient.invalidateQueries(["userTasks"]);
      queryClient.invalidateQueries(["dateTasks"]);
      notifySuccess("Task updated successfully");
    },
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to update task");
    },
  });
};

// Delete Task
export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  const token = localStorage.getItem("token");

  return useMutation({
    mutationFn: async (taskId) => {
      const res = await axios.delete(`${BASE_URL}/task/delete-task/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    },
    onSuccess: () => {
      // Invalidate all queries that start with these keys
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["userTasks"] });
      queryClient.invalidateQueries({ queryKey: ["dateTasks"] });
      queryClient.invalidateQueries({ queryKey: ["departmentTasks"] });

      notifySuccess("Task deleted successfully");
    },
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to delete task");
    },
  });
};

// Apply Leave
export const useApplyLeave = () => {
  const queryClient = useQueryClient();
  const token = localStorage.getItem("token");

  return useMutation({
    mutationFn: async ({ date, userId }) => {
      const res = await axios.post(
        `${BASE_URL}/task/apply-leave`,
        { date, userId }, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["tasks"]);
      queryClient.invalidateQueries(["userTasks"]);
      queryClient.invalidateQueries(["dateTasks"]);
      notifySuccess(data?.message || "Leave applied successfully");
    },
    onError: (error) => {
      notifyError(error?.response?.data?.message || "Failed to apply leave");
    },
  });
};
